import * as React from "react";
import { request } from "../services/cpln";

export interface GeoRouteEntry {
  location: string;
  target: string;
  reason: string;
  latencyMs: number | null;
}

interface InstantQueryResponse {
  status: string;
  data: {
    resultType: string;
    result: {
      metric: Record<string, string>;
      value: [number, string];
    }[];
  };
}

interface UseGeoRouteResult {
  data: GeoRouteEntry[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  query: string;
  rawResponse: any;
}

export function useGeoRoute(org: string, endpoint: string): UseGeoRouteResult {
  const [data, setData] = React.useState<GeoRouteEntry[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rawResponse, setRawResponse] = React.useState<any>(null);

  const query = React.useMemo(() => {
    if (!org || !endpoint) return "";
    return `max(geo_route{endpoint="${endpoint}"}) by (endpoint, location, reason, target) > 0`;
  }, [org, endpoint]);

  const fetchData = React.useCallback(async () => {
    if (!org || !endpoint) {
      setData([]);
      setRawResponse(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await request<InstantQueryResponse>({
        service: "metrics",
        method: "post",
        url: `/metrics/org/${org}/api/v1/query?query=${encodeURIComponent(query)}`,
      });

      setRawResponse(response.data);

      if (response.data.status !== "success") {
        setError((response.data as any).error || "Metrics API returned an error");
        return;
      }

      const result = response.data.data?.result || [];
      const entries: GeoRouteEntry[] = result.map((item) => {
        const raw = parseFloat(item.value[1]);
        return {
          location: item.metric.location || "",
          target: item.metric.target || "",
          reason: item.metric.reason || "",
          latencyMs: isNaN(raw) ? null : raw,
        };
      });

      // Sort by location name
      entries.sort((a, b) => a.location.localeCompare(b.location));
      setData(entries);
    } catch (err: any) {
      console.error("Failed to fetch geo route metrics:", err);
      const message = err?.response?.data?.error || err?.message || "Failed to fetch geo route data";
      setError(message);
      setRawResponse(null);
    } finally {
      setIsLoading(false);
    }
  }, [org, endpoint, query]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData, query, rawResponse };
}
